import {
  Title,
  Group,
  ActionIcon,
  Table,
  Button,
  Box,
  Modal, // Added Modal
  Text,
  Code,
  CopyButton, // Added for displaying text in table
} from "@mantine/core";
import {
  IconTrash,
  IconDeviceFloppy,
  IconPencil,
  IconPlus,
  IconCopy,
} from "@tabler/icons-react"; // Added IconPencil, IconPlus
import { useForm } from "@mantine/form";
import { useEffect, useState } from "react"; // Added useState
import { notifications } from "@mantine/notifications";
import {
  adminDeletePluginOptionValue,
  adminUpdatePluginOptionValue,
  adminCreatePluginOptionValue, // Added placeholder for create API
} from "../../api";
import PluginOptionValueEditModal from "./PluginOptionValueEditModal"; // Placeholder for the new modal component

// TODOS:
// Make this editable - IN PROGRESS (modal approach)
// Have two different display modes, one for simple styles and one for complex ones with Loras - (Partially addressed by modal)
// Add a button to add new Loras - (Will be in the modal)
// Add a button to add new Tags - (If applicable, will be in the modal)
// Add a button to remove options - (This component is for values of an option)
// add a button to add new options - (This component is for values of an option)

export default function AdminPluginOptionEdit({
  option,
  values,
  onClose,
  pluginId,
}) {
  console.debug(
    "AdminPluginOptionEdit rendered with option:",
    option,
    "and values:",
    values
  );

  const [editModalOpened, setEditModalOpened] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isCreatingNewValue, setIsCreatingNewValue] = useState(false);
  const [nextTemporaryId, setNextTemporaryId] = useState(0); // For new items before saving

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      optionValuesList: [],
    },
  });

  // Synchronize the `values` prop with the form's internal state
  useEffect(() => {
    if (values) {
      // Ensure all items have a unique key, assign temporary if new from backend without ID
      const processedValues = values.map((v, i) => ({
        ...v,
        // Use existing id, or a temporary client-side id if it's a new item not yet saved
        // This helps in managing items in the list before they are persisted
        _internal_id: v.id || `temp-${i}-${Date.now()}`,
      }));
      form.setValues({
        optionValuesList: JSON.parse(JSON.stringify(processedValues)),
      });
    } else {
      form.setValues({ optionValuesList: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]); // form.setValues is stable, only re-run if values prop changes.

  const handleOpenEditModal = (item) => {
    setEditingItem(JSON.parse(JSON.stringify(item))); // Deep copy to avoid direct state mutation
    setIsCreatingNewValue(false);
    setEditModalOpened(true);
  };

  const handleOpenCreateModal = () => {
    setEditingItem({
      // Default structure for a new value
      _internal_id: `new-${nextTemporaryId}`, // Temporary ID for new items
      titleUser: "",
      prompt: "",
      loras: [],
      previewImageUrl: "", // Add other fields as necessary
      // Ensure new items have all fields the modal expects
    });
    setNextTemporaryId((prev) => prev + 1);
    setIsCreatingNewValue(true);
    setEditModalOpened(true);
  };

  const handleSaveValueFromModal = (savedItem) => {
    const currentList = form.getValues()?.optionValuesList;
    if (isCreatingNewValue) {
      // Add new item to the list
      form.setFieldValue("optionValuesList", [...currentList, savedItem]);
    } else {
      // Update existing item in the list
      const itemIndex = currentList.findIndex(
        (val) => val._internal_id === savedItem._internal_id
      );
      if (itemIndex !== -1) {
        form.setFieldValue(`optionValuesList.${itemIndex}`, savedItem);
      } else {
        // Fallback if item not found, though this shouldn't happen
        form.setFieldValue("optionValuesList", [...currentList, savedItem]);
      }
    }
    setEditModalOpened(false);
    setEditingItem(null);
  };

  const handleSubmit = async (formSubmitValues) => {
    console.debug(
      "handleSubmit invoked with:",
      formSubmitValues.optionValuesList
    );
    let allSucceeded = true;
    const results = []; // To store results of API calls

    for (const valueItem of formSubmitValues.optionValuesList) {
      try {
        let result;
        if (valueItem.id) {
          // Existing item, call update
          result = await adminUpdatePluginOptionValue(
            pluginId,
            option?.id,
            valueItem.id,
            valueItem
          );
        } else {
          // New item, call create
          // Ensure we don't send _internal_id to backend if it's not a real field
          const { _internal_id, ...itemToCreate } = valueItem;
          result = await adminCreatePluginOptionValue(
            pluginId,
            option?.id,
            itemToCreate
          );
        }
        results.push({ ...valueItem, ...result }); // Update item with result from API (e.g., new ID)
      } catch (error) {
        allSucceeded = false;
        console.error(
          "Failed to save plugin option value:",
          valueItem._internal_id || valueItem.id,
          error
        );
        notifications.show({
          title: "Save Failed for an Item",
          message: `Could not save value "${
            valueItem.titleUser || valueItem._internal_id
          }": ${error.message}`,
          color: "red",
        });
      }
    }

    if (allSucceeded) {
      notifications.show({
        title: "Options Saved",
        message: "Plugin option values have been saved successfully.",
        color: "teal",
      });
      // Update form with potentially new IDs from backend
      form.setValues({
        optionValuesList: JSON.parse(
          JSON.stringify(
            results.map((r) => ({ ...r, _internal_id: r.id || r._internal_id }))
          )
        ),
      });
      if (onClose) onClose();
    } else {
      notifications.show({
        title: "Partial Save",
        message:
          "Some plugin option values could not be saved. Check console for details.",
        color: "orange",
      });
      // Update form with results that did succeed, potentially with new IDs
      form.setValues({
        optionValuesList: JSON.parse(
          JSON.stringify(
            results.map((r) => ({ ...r, _internal_id: r.id || r._internal_id }))
          )
        ),
      });
    }
  };

  const handleDeleteValue = async (
    indexToDelete,
    valueIdToDelete,
    internalId
  ) => {
    if (!window.confirm("Are you sure you want to delete this value?")) {
      return;
    }
    try {
      if (valueIdToDelete) {
        // Only call API if it's a persisted item
        await adminDeletePluginOptionValue(
          pluginId,
          option?.id,
          valueIdToDelete
        );
      }
      // Remove from form list using internalId for consistency
      const updatedList = form
        .getValues()
        ?.optionValuesList.filter((item) => item._internal_id !== internalId);
      form.setValues({ optionValuesList: updatedList });

      notifications.show({
        title: "Value Deleted",
        message:
          "The plugin option value has been deleted successfully from the list.",
        color: "green",
      });
    } catch (error) {
      console.error("Failed to delete plugin option value from API:", error);
      notifications.show({
        title: "Delete Failed",
        message: `Could not delete plugin option value from API: ${error.message}`,
        color: "red",
      });
    }
  };

  const currentValues = form.getValues()?.optionValuesList || [];

  return (
    <Box p="md">
      {/* Removed <Code block>{JSON.stringify(currentValues, null, 2)}</Code> */}
      <Group justify="space-between" mb="lg">
        <Title order={2}>
          Edit Values for: {option?.title || option?.name || "Selected Option"}
          {/* {option?.mediaClass && <Code>{option.mediaClass}</Code>} */}
        </Title>
        <Button
          variant="default"
          onClick={handleOpenCreateModal}
          leftSection={<IconPlus size={18} />}
        >
          Add New Value
        </Button>
      </Group>

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Table
          striped
          highlightOnHover
          withTableBorder
          withColumnBorders
          mb="md"
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: "35%" }}>Title</Table.Th>
              <Table.Th style={{ width: "10%" }}>Base Model</Table.Th>
              <Table.Th style={{ width: "10%" }}>Prompt length</Table.Th>
              <Table.Th style={{ width: "10%", textAlign: "center" }}>
                Loras
              </Table.Th>
              <Table.Th style={{ width: "5%", textAlign: "center" }}>
                Preview
              </Table.Th>
              <Table.Th style={{ width: "10%", textAlign: "center" }}>
                Actions
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {currentValues && currentValues.length > 0 ? (
              currentValues.map((item, index) => (
                <Table.Tr key={item._internal_id}>
                  <Table.Td>
                    <Text size="sm">{item.titleUser || "(No title)"}</Text>
                    <Group gap="xs">
                      <Code style={{ fontSize: "10px" }}>{item.lora}</Code>
                      <CopyButton value={item.lora}>
                        {({ copied, copy }) => (
                          <ActionIcon
                            size="sm"
                            color={copied ? "teal" : "default"}
                            onClick={copy}
                          >
                            <IconCopy size={14} />
                          </ActionIcon>
                        )}
                      </CopyButton>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{item.baseModel}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    {item.prompt?.length || "(No prompt)"}
                  </Table.Td>
                  <Table.Td style={{ textAlign: "center" }}>
                    {Array.isArray(item.loras)
                      ? item.loras.length
                      : item.lora !== null
                      ? 1
                      : 0}
                  </Table.Td>
                  <Table.Td style={{ textAlign: "center" }}>
                    {item.previewImageUrl ? "Yes" : "No"}
                  </Table.Td>
                  <Table.Td style={{ textAlign: "center" }}>
                    <Group gap="xs" justify="center" wrap="nowrap">
                      <ActionIcon
                        variant="subtle"
                        onClick={() => handleOpenEditModal(item)}
                        color="blue"
                        title="Edit Value"
                      >
                        <IconPencil size={20} stroke={1.5} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        onClick={() =>
                          handleDeleteValue(index, item.id, item._internal_id)
                        }
                        color="red"
                        title="Delete Value"
                      >
                        <IconTrash size={20} stroke={1.5} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))
            ) : (
              <Table.Tr>
                <Table.Td colSpan={5} align="center">
                  No option values to display. Click "Add New Value" to create
                  one.
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
        <Group justify="flex-end" mt="xl">
          {onClose && (
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
          )}
          <Button type="submit" leftSection={<IconDeviceFloppy size={18} />}>
            Save All Changes
          </Button>
        </Group>
      </form>

      {editingItem && (
        <PluginOptionValueEditModal
          opened={editModalOpened}
          onClose={() => {
            setEditModalOpened(false);
            setEditingItem(null);
          }}
          valueData={editingItem}
          onSave={handleSaveValueFromModal}
          isCreating={isCreatingNewValue}
          pluginId={pluginId}
          optionId={option?.id}
        />
      )}
    </Box>
  );
}
